import { Minus, Plus, RotateCcw } from "lucide-react-native";
import {
  ComponentRef,
  FC,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { View } from "react-native";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";

import { Box, Pressable, Text } from "@/components/ui";
import type { CountryValue } from "@/types/resources";

import {
  BASE_CENTER,
  BASE_SCALE,
  CONTENT_HEIGHT,
  DRAG_SENSITIVITY,
  GEO_URL,
  NUMERIC_TO_ISO2,
} from "../constants";
function worldCountryGeographyStyle(
  isSelected: boolean,
  isAvailable: boolean,
  disabled: boolean,
  strokeWidth: number
) {
  return {
    default: {
      cursor: disabled ? "default" : isAvailable ? "pointer" : "grab",
      fill: isSelected
        ? "rgb(var(--color-primary-500))"
        : isAvailable
          ? "rgb(var(--color-primary-200))"
          : "#e5e7eb",
      outline: "none",
      stroke: "#fff",
      strokeWidth,
    },
    hover: {
      fill: isSelected
        ? "rgb(var(--color-primary-600))"
        : isAvailable
          ? "rgb(var(--color-primary-300))"
          : "#e5e7eb",
      outline: "none",
      stroke: "#fff",
      strokeWidth,
    },
    pressed: {
      fill: isSelected
        ? "rgb(var(--color-primary-600))"
        : "rgb(var(--color-primary-300))",
      outline: "none",
      stroke: "#fff",
      strokeWidth,
    },
  } as const;
}

interface FocusRegion {
  center: [number, number];
  scale: number;
}

export interface WorldMapProps {
  countryValues: CountryValue[];
  disabled: boolean;
  focusRegion?: FocusRegion | null;
  itemCodes: Set<CountryValue>;
  onToggleCountry: (code: CountryValue) => void;
}

const STROKE_WIDTH = 0.75;

/* The map SVG viewBox is 800 × CONTENT_HEIGHT. Sizing the container by this
   aspect ratio (instead of a fixed pixel height) keeps the map proportionally
   identical on every screen — full size on desktop, scaled down on mobile —
   instead of letterboxing into a tall, mostly-empty box on narrow widths. */
const MAP_ASPECT = 800 / CONTENT_HEIGHT;

export const WorldMap: FC<WorldMapProps> = ({
  countryValues,
  disabled,
  focusRegion,
  itemCodes,
  onToggleCountry,
}) => {
  const [mapScale, setMapScale] = useState(BASE_SCALE);
  const [mapCenter, setMapCenter] = useState<[number, number]>(BASE_CENTER);
  const [tooltip, setTooltip] = useState<{
    label: string;
    x: number;
    y: number;
  } | null>(null);

  const dragging = useRef(false);
  // Mirror the drag state for the rendered cursor (a ref can't be read during
  // render). The ref drives the mousemove hot path; the state drives the style.
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const centerAtDragStart = useRef<[number, number]>(BASE_CENTER);
  const didDrag = useRef(false);
  const mapContainerRef = useRef<ComponentRef<typeof View>>(null);
  const wheelRef = useRef<ComponentRef<typeof View>>(null);

  // Sync the view from the focused region during render (not in an effect) when
  // it changes; the initial values are already seeded to the base above.
  const [prevFocusRegion, setPrevFocusRegion] = useState(focusRegion);
  if (prevFocusRegion !== focusRegion) {
    setPrevFocusRegion(focusRegion);
    if (focusRegion) {
      setMapCenter(focusRegion.center);
      setMapScale(focusRegion.scale);
    } else {
      setMapCenter(BASE_CENTER);
      setMapScale(BASE_SCALE);
    }
  }

  useEffect(() => {
    const el = wheelRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setMapScale((s) =>
        Math.max(
          BASE_SCALE,
          Math.min(e.deltaY < 0 ? s * 1.15 : s / 1.15, BASE_SCALE * 12)
        )
      );
    };
    // @ts-expect-error web-only wheel event
    el.addEventListener("wheel", onWheel, { passive: false });
    // @ts-expect-error web-only wheel event
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      dragging.current = true;
      setIsDragging(true);
      didDrag.current = false;
      dragStart.current = { x: e.clientX, y: e.clientY };
      centerAtDragStart.current = mapCenter;
    },
    [mapCenter]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - dragStart.current.x;
      const dy = e.clientY - dragStart.current.y;
      if (Math.abs(dx) > 3 || Math.abs(dy) > 3) didDrag.current = true;
      const factor = DRAG_SENSITIVITY * (BASE_SCALE / mapScale);
      setMapCenter([
        centerAtDragStart.current[0] - dx * factor,
        centerAtDragStart.current[1] + dy * factor,
      ]);
    },
    [mapScale]
  );

  const handleMouseUp = useCallback(() => {
    dragging.current = false;
    setIsDragging(false);
  }, []);

  const handleZoomIn = useCallback(() => {
    setMapScale((s) => Math.min(s * 1.15, BASE_SCALE * 12));
  }, []);

  const handleZoomOut = useCallback(() => {
    setMapScale((s) => Math.max(s / 1.15, BASE_SCALE));
  }, []);

  const handleReset = useCallback(() => {
    setMapScale(BASE_SCALE);
    setMapCenter(BASE_CENTER);
  }, []);

  const zoomPct = Math.round((mapScale / BASE_SCALE) * 100);

  return (
    <Box ref={wheelRef} style={{ position: "relative" }}>
      <Box
        ref={mapContainerRef}
        onMouseDown={handleMouseDown}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        // @ts-expect-error web-only CSS
        style={{
          aspectRatio: MAP_ASPECT,
          backgroundColor: "rgb(var(--color-background-0))",
          cursor: disabled ? "default" : isDragging ? "grabbing" : "grab",
          overflow: "hidden",
          padding: 8,
          userSelect: "none",
          width: "100%",
        }}
      >
        <ComposableMap
          height={CONTENT_HEIGHT}
          projectionConfig={{ center: mapCenter, scale: mapScale }}
          style={{
            display: "block",
            height: "100%",
            width: "100%",
          }}
        >
          <Geographies geography={GEO_URL}>
            {({
              geographies,
            }: {
              geographies: {
                id: string;
                properties: { name: string };
                rsmKey: string;
              }[];
            }) =>
              geographies.map((geo) => {
                const iso2 = NUMERIC_TO_ISO2[geo.id];
                const isSelected = iso2 && countryValues.includes(iso2);
                const isAvailable = iso2 && itemCodes.has(iso2);
                return (
                  <Geography
                    geography={geo}
                    key={geo.rsmKey}
                    onClick={() => {
                      if (!didDrag.current && isAvailable && iso2)
                        onToggleCountry(iso2);
                    }}
                    onMouseEnter={(e) =>
                      setTooltip({
                        label: geo.properties.name,
                        x: e.clientX,
                        y: e.clientY,
                      })
                    }
                    onMouseLeave={() => setTooltip(null)}
                    onMouseMove={(e) =>
                      setTooltip({
                        label: geo.properties.name,
                        x: e.clientX,
                        y: e.clientY,
                      })
                    }
                    style={worldCountryGeographyStyle(
                      !!isSelected,
                      !!isAvailable,
                      disabled,
                      STROKE_WIDTH
                    )}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </Box>

      <Box
        style={{
          bottom: 12,
          display: "flex",
          flexDirection: "column",
          gap: 4,
          position: "absolute",
          right: 12,
        }}
      >
        <Pressable onPress={handleZoomIn}>
          <Box className="w-7 h-7 rounded-md border border-outline-200 bg-white items-center justify-center">
            <Plus color="rgb(var(--color-typography-600))" size={14} />
          </Box>
        </Pressable>
        <Box className="h-7 items-center justify-center">
          <Text className="text-xs text-typography-500 font-mono">
            {zoomPct}%
          </Text>
        </Box>
        <Pressable onPress={handleZoomOut}>
          <Box className="w-7 h-7 rounded-md border border-outline-200 bg-white items-center justify-center">
            <Minus color="rgb(var(--color-typography-600))" size={14} />
          </Box>
        </Pressable>
        <Pressable onPress={handleReset}>
          <Box className="w-7 h-7 rounded-md border border-outline-200 bg-white items-center justify-center">
            <RotateCcw color="rgb(var(--color-typography-600))" size={12} />
          </Box>
        </Pressable>
      </Box>

      {tooltip &&
        createPortal(
          <div
            style={{
              background: "rgb(var(--color-background-800))",
              borderRadius: 6,
              color: "rgb(var(--color-typography-0))",
              fontSize: 12,
              left: tooltip.x + 12,
              padding: "4px 8px",
              pointerEvents: "none",
              position: "fixed",
              top: tooltip.y + 12,
              whiteSpace: "nowrap",
              zIndex: 9999,
            }}
          >
            {tooltip.label}
          </div>,
          document.body
        )}
    </Box>
  );
};
